import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { getCities, normalizeCitiesFromApiResponse } from "../api";

const NAMES = [
  "Hyderabad",
  "Bangalore",
  "Mumbai",
  "Chennai",
  "Delhi",
  "Pune",
  "Kolkata",
  "Ahmedabad",
  "Noida",
  "Jaipur",
  "Indore",
  "Surat",
  "Nagpur",
  "Vizag",
];

function buildFallbackList() {
  const list = NAMES.map((name) => ({
    name,
    slug: name.trim().toLowerCase().replace(/\s+/g, "-"),
    image_url: null,
  }));
  list.sort((a, b) => (a.name || "").localeCompare(b.name || "", undefined, { sensitivity: "base" }));
  return list;
}

const CitiesContext = createContext(null);

export function CitiesProvider({ children }) {
  const [cities, setCities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await getCities();
        const list = normalizeCitiesFromApiResponse(res);
        if (cancelled) return;
        if (list.length === 0) {
          setCities(buildFallbackList());
        } else {
          list.sort((a, b) => (a.name || "").localeCompare(b.name || "", undefined, { sensitivity: "base" }));
          setCities(list);
        }
      } catch (e) {
        if (!cancelled) {
          console.error("CitiesProvider:", e);
          setError(e);
          setCities(buildFallbackList());
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const value = useMemo(() => ({ cities, loading, error }), [cities, loading, error]);

  return <CitiesContext.Provider value={value}>{children}</CitiesContext.Provider>;
}

export function useCities() {
  const ctx = useContext(CitiesContext);
  if (!ctx) {
    throw new Error("useCities must be used within CitiesProvider");
  }
  return ctx;
}

/** For rare cases outside the provider (tests); prefer CitiesProvider at app root. */
export function useCitiesOptional() {
  return useContext(CitiesContext);
}
